import { Alert, Badge, Stack, Table, Text } from '@mantine/core'
import { IconEyeOff } from '@tabler/icons-react'
import { MetaList, SectionCard } from '@lighthouse/design'
import { useReportDetailUsage } from '../../hooks/useReportDetail'
import { NOT_MEASURED, exactNumber, whenLabel } from '../../utils/format'
import type { ReportDetailWindow as WindowDays } from '../../utils/reportDetailPath'
import type {
  ReportDetailOutcomeWindow, ReportDetailPeople, ReportDetailReport, ReportDetailScheduledIdentity,
  ReportDetailSubscriptions, ReportDetailUsage,
} from '../../types/reportDetail'
import { Caption, EvaluatedAt, Figure, TabFailure, TabLoading, WindowBanner } from './OverviewTab'
import { OutcomesBanner } from './OperationsTab'
import {
  NAMES_WITHHELD,
  WOULD_DELIVER_LABEL,
  activeDaysLabel,
  countText,
  interactiveView,
  INTERACTIVE_LABEL,
  INTERACTIVE_NOT_RECORDED,
  lastExecutionLabel,
  notDeliveringText,
  outcomeColumnHeader,
  outcomeCountView,
  personActiveDaysHeader,
  recipientsView,
  showingText,
  windowTitle,
} from './reportDetailFormat'

/**
 * Report Detail → Usage: who ran it and how often — RUNS RECORDED, NOT VIEWS.
 *
 * Before every open of a saved report was recorded, an unparameterized report
 * opened in Spyglass recorded nothing, and nobody reading a delivered workbook is
 * recorded at all; the server's caption says so at the top. ROWS are counted over the whole window and say so: the log's
 * older rows are grid pages and delivery attempts. OPENS and DELIVERIES are
 * counted only inside the outcome window (decision R2, 2026-10-01), and are
 * NOT_MEASURED with the server's reason while it has none — never 0. Runs in a
 * personal scope are counted and never named. Scheduled deliveries are the
 * identities they ran as, apart from people.
 *
 * `report` is the Overview's, for the status a schedule would deliver under; it
 * may still be loading, and then the not-delivering line is simply not drawn.
 */
export function UsageTab({ reportId, days, report }: { reportId: string; days: WindowDays; report?: ReportDetailReport | null }) {
  const { data, isLoading, isError, error } = useReportDetailUsage(reportId, days, { open: true })

  if (isError) return <TabFailure error={error} what="this report’s usage" />
  if (isLoading || !data) return <TabLoading />

  return <UsageBody usage={data} report={report ?? null} />
}

/** Why an open count is not measured: the recording first, then the outcome window. */
function opensReason(recordable: boolean, outcomes: ReportDetailOutcomeWindow): string | null {
  return recordable ? outcomes.unavailableReason : INTERACTIVE_NOT_RECORDED
}

function UsageBody({ usage, report }: { usage: ReportDetailUsage; report: ReportDetailReport | null }) {
  const heading = windowTitle(usage.window)
  const totals = usage.totals
  const emptyReason = totals ? null : 'the run log is empty'
  const interactive = interactiveView(totals?.interactiveRows, usage.interactiveRecordable, emptyReason)
  const opens = usage.interactiveRecordable
    ? outcomeCountView(totals?.interactiveOpens, emptyReason ?? usage.outcomes.unavailableReason)
    : { value: NOT_MEASURED, reason: INTERACTIVE_NOT_RECORDED }
  const deliveries = outcomeCountView(totals?.scheduledDeliveries, emptyReason ?? usage.outcomes.unavailableReason)

  return (
    <Stack gap="lg">
      <Alert variant="light" color="gray" icon={<IconEyeOff size={16} />} title="Runs recorded, not views" data-state="recorded-not-views">
        <Text size="sm">{usage.recordedNotViewsCaption}</Text>
      </Alert>

      <WindowBanner window={usage.window} />
      <OutcomesBanner outcomes={usage.outcomes} window={usage.window} />

      <SectionCard title={`Totals — ${heading}`} subtitle="Every row of this report in the window, both scopes, by source">
        <Stack gap="sm">
          <MetaList
            items={[
              { label: `Interactive — ${INTERACTIVE_LABEL}`, value: <Figure value={interactive.value} reason={interactive.reason} /> },
              { label: `Interactive ${outcomeColumnHeader('opens', usage.outcomes)}`, value: <Figure value={opens.value} reason={opens.reason} /> },
              { label: 'Scheduled rows recorded', value: countText(totals?.scheduledRows) },
              { label: `Scheduled ${outcomeColumnHeader('deliveries', usage.outcomes)}`, value: <Figure value={deliveries.value} reason={deliveries.reason} /> },
              { label: 'Exports', value: countText(totals?.exports) },
              { label: activeDaysLabel(usage.interactiveRecordable), value: countText(totals?.activeDays) },
            ]}
          />
          <Caption>
            Opens and deliveries count each one once, whatever its outcome; rows count what the log wrote, which in its
            older rows was every grid page and every delivery attempt.
          </Caption>
          <Caption>{usage.interactiveRecordableReason}</Caption>
          <Caption>
            {lastExecutionLabel(usage.interactiveRecordable)}: {whenLabel(usage.lastExecutionAt)}
            {usage.lastExecutionIsBeforeWindow === true && ' — before this window'}
          </Caption>
        </Stack>
      </SectionCard>

      <SectionCard title={`People — ${heading}`} subtitle="Shared-scope runs, keyed on the workspace each run happened in">
        <PeopleSection people={usage.people} recordable={usage.interactiveRecordable} outcomes={usage.outcomes} />
      </SectionCard>

      <SectionCard title={`Personal scope — ${heading}`} subtitle="Runs whose run-time workspace was personal: counted, never named">
        {usage.personalScope == null ? (
          <Figure value={NOT_MEASURED} reason="the run log is empty" />
        ) : (
          <Stack gap={4}>
            <Text size="sm" data-figure="personal-scope">
              {exactNumber(usage.personalScope.distinctPeopleCount)}{' '}
              {usage.personalScope.distinctPeopleCount === 1 ? 'person' : 'people'},{' '}
              {exactNumber(usage.personalScope.rowCount)} {usage.personalScope.rowCount === 1 ? 'row' : 'rows'} — {NAMES_WITHHELD}
            </Text>
            <Caption>{usage.personalScope.caption}</Caption>
          </Stack>
        )}
      </SectionCard>

      <SectionCard title={`Scheduled deliveries ran as — ${heading}`} subtitle="Run-as identities, not readers">
        <Stack gap="sm">
          <IdentitiesTable identities={usage.scheduledIdentities} outcomes={usage.outcomes} />
          <Caption>{usage.scheduledIdentitiesCaption}</Caption>
        </Stack>
      </SectionCard>

      <SectionCard title="Favourites" subtitle="Now, not in the window">
        <Stack gap={4}>
          <Text size="sm">
            {exactNumber(usage.favourites.count)} — newest {whenLabel(usage.favourites.newestAt)}
          </Text>
          <Caption>{usage.favourites.caption}</Caption>
        </Stack>
      </SectionCard>

      <SectionCard title="Subscriptions" subtitle="Each schedule, as a subscription — now, not in the window">
        <SubscriptionsSection subscriptions={usage.subscriptions} report={report} />
      </SectionCard>

      <EvaluatedAt at={usage.evaluatedAt} />
    </Stack>
  )
}

function PeopleSection({ people, recordable, outcomes }: {
  people: ReportDetailPeople | null
  recordable: boolean
  outcomes: ReportDetailOutcomeWindow
}) {
  if (people == null) return <Figure value={NOT_MEASURED} reason="the run log is empty" />


  return (
    <Stack gap="sm">
      <Caption>
        {showingText(people.shownCount, people.distinctPeopleCount)} {people.distinctPeopleCount === 1 ? 'person' : 'people'}.{' '}
        {people.caption}
      </Caption>
      {outcomes.from == null && recordable && <Caption>Opens: {NOT_MEASURED} — {opensReason(recordable, outcomes)}</Caption>}
      {people.rows.length === 0 ? (
        <Text size="sm" c="dimmed">No shared-scope run or export of this report is recorded in this window.</Text>
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Person</Table.Th>
                <Table.Th>{personActiveDaysHeader(recordable)}</Table.Th>
                <Table.Th>Interactive rows</Table.Th>
                <Table.Th>{outcomeColumnHeader('Opens', outcomes)}</Table.Th>
                <Table.Th>Exports</Table.Th>
                <Table.Th>Last</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {people.rows.map((person) => (
                <Table.Tr key={person.userId}>
                  <Table.Td>
                    <Text size="sm">{person.displayName ?? `${person.userId} (name not stamped)`}</Text>
                    {person.isOwner && <Badge size="xs" variant="light" color="gray">owner</Badge>}
                  </Table.Td>
                  <Table.Td>{exactNumber(person.activeDays)}</Table.Td>
                  <Table.Td>{interactiveView(person.interactiveRows, recordable, null).value}</Table.Td>
                  <Table.Td>{recordable ? outcomeCountView(person.interactiveOpens, null).value : NOT_MEASURED}</Table.Td>
                  <Table.Td>{exactNumber(person.exportRuns)}</Table.Td>
                  <Table.Td>{whenLabel(person.lastAt)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  )
}

function IdentitiesTable({ identities, outcomes }: {
  identities: ReportDetailScheduledIdentity[] | null
  outcomes: ReportDetailOutcomeWindow
}) {
  if (identities == null) return <Figure value={NOT_MEASURED} reason="the run log is empty" />
  if (identities.length === 0) {
    return <Text size="sm" c="dimmed">No shared-scope scheduled delivery of this report is recorded in this window.</Text>
  }
  return (
    <Stack gap="xs">
      {outcomes.from == null && <Caption>Deliveries: {NOT_MEASURED} — {outcomes.unavailableReason}</Caption>}
      <Table.ScrollContainer minWidth={560}>
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Ran as</Table.Th>
              <Table.Th>Scheduled rows</Table.Th>
              <Table.Th>{outcomeColumnHeader('Deliveries', outcomes)}</Table.Th>
              <Table.Th>Last</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {identities.map((identity) => (
              <Table.Tr key={identity.runAsUserId}>
                <Table.Td>{identity.runAsDisplayName ?? `${identity.runAsUserId} (name not stamped)`}</Table.Td>
                <Table.Td>{exactNumber(identity.scheduledRows)}</Table.Td>
                <Table.Td>{outcomeCountView(identity.deliveries, null).value}</Table.Td>
                <Table.Td>{whenLabel(identity.lastAt)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  )
}

function SubscriptionsSection({ subscriptions, report }: { subscriptions: ReportDetailSubscriptions; report: ReportDetailReport | null }) {
  const recipients = recipientsView(subscriptions)
  const notDelivering = notDeliveringText(report)

  return (
    <Stack gap="sm">
      {notDelivering && (
        <Text size="sm" fw={500} data-state="not-delivering">
          {exactNumber(subscriptions.activeSchedules)} active {subscriptions.activeSchedules === 1 ? 'schedule' : 'schedules'} {notDelivering}.
        </Text>
      )}
      <MetaList
        items={[
          { label: 'Active schedules', value: exactNumber(subscriptions.activeSchedules) },
          { label: 'Paused schedules', value: exactNumber(subscriptions.pausedSchedules) },
          { label: WOULD_DELIVER_LABEL, value: exactNumber(subscriptions.deliveringSchedules) },
          { label: 'Distinct recipient addresses', value: <Figure value={recipients.value} reason={recipients.reason} /> },
          {
            label: 'Recipient slots',
            value: subscriptions.recipientSlots == null
              ? <Figure value={NOT_MEASURED} reason="not counted while a list is unreadable" />
              : exactNumber(subscriptions.recipientSlots),
          },
          { label: 'Distinct run-as identities', value: exactNumber(subscriptions.distinctRunAsIdentities) },
        ]}
      />
      <Caption>{subscriptions.caption}</Caption>
    </Stack>
  )
}
